import React, { useState, useEffect, useCallback, useRef, useMemo  } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useCart } from '../components/CartContext';
import { Helmet } from 'react-helmet';
import { createProductUrl } from '../utils/urlSlugs';
import { FaStar, FaStarHalfAlt, FaRegStar, FaShippingFast, FaCheck, FaExchangeAlt, FaShare, FaFacebook, FaTwitter, FaInstagram, FaYoutube, FaGift, FaHeart, FaClock, FaShieldAlt, FaTruck, FaInfoCircle, FaShoppingCart } from 'react-icons/fa';
import translations from '../utils/data';
import PageHeader from './Other';
import amazon from '../assets/amazon.webp';
import { FaQuestionCircle } from 'react-icons/fa';
import BusinessSchema from './BusinessSchema';

const ReviewsSlider = ({ reviews, renderStars, formatDate }) => {
  const total = reviews?.length || 0;
  const isCompact = total > 0 && total <= 5;
  const [page, setPage] = useState(0);             // page index (0..pages-1)
  const [spv, setSpv] = useState(1);               // slides per view (responsive)
  const [paused, setPaused] = useState(false);

  const startXRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Responsive slides-per-view similar to product card rows
    const computeSPV = useCallback(() => {
    const w = window.innerWidth;
    if (w >= 1440) return 4;     // xl+: show 4 wide
    if (w >= 1024) return 3;     // lg: show 3 wide
    if (w >= 740)  return 2;     // sm/md: show 2 wide
    return 1;                    // xs: show 1
    }, []);

  useEffect(() => {
    const apply = () => setSpv(computeSPV());
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, [computeSPV]);

  const pages = Math.max(1, Math.ceil(total / spv));

  const nextPage = () => setPage((p) => (p + 1) % pages);
  const prevPage = () => setPage((p) => (p - 1 + pages) % pages);
  const goToPage = (i) => setPage(i);

  // Autoplay with cleanup
  useEffect(() => {
    if (pages <= 1 || paused) return;
    const id = setInterval(nextPage, 4000);
    return () => clearInterval(id);
  }, [pages, paused]);

  // Keyboard navigation
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") nextPage();
    if (e.key === "ArrowLeft") prevPage();
  };

  // Swipe gestures
  const onPointerDown = (e) => {
    isDraggingRef.current = true;
    startXRef.current = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
  };
  const onPointerUp = (e) => {
    if (!isDraggingRef.current) return;
    const endX = e.clientX ?? e.changedTouches?.[0]?.clientX ?? 0;
    const delta = endX - (startXRef.current || 0);
    isDraggingRef.current = false;
    startXRef.current = null;
    const threshold = 50;
    if (delta < -threshold) nextPage();
    if (delta > threshold) prevPage();
  };

    if (!total) return null;

    const slideWidth = `${100 / spv}%`;

    const preview = (txt) => (txt?.length > 140 ? `${txt.slice(0, 140)}…` : txt || "");
    // Add near the top of ReviewsSlider (after hooks)
    const getPrimaryImage = (r) => {
            if (!r) return null;
            return Array.isArray(r.image) ? r.image.find(Boolean) : r.image || null;
    };

    const getFallbackImage = (idx) => {
        // 1) Try current review image
        let src = getPrimaryImage(reviews[idx]);
        if (src) return src;

        // 2) Fallback to previous review image (walk backward)
        for (let j = idx - 1; j >= 0; j--) {
            const prev = getPrimaryImage(reviews[j]);
            if (prev) return prev;
        }

        // 3) Final fallback: return null (keeps “No image” block)
        return null;
    };

    if (isCompact) {
        return (
            <section
            className="relative"
            aria-label="Customer reviews"
            role="region"
            tabIndex={0}
            >
            <div role="list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                {reviews.map((review, idx) => {
                const imgSrc = getPrimaryImage(review);
                const textOnly = !imgSrc;

                return (
                    <article key={review.id} role="listitem" className="px-1">
                    <div
                        className={[
                        "rounded-lg overflow-hidden shadow-sm border transition-colors",
                        textOnly
                            ? "bg-blue-50 border-blue-200 border-l-4 border-l-blue-500"
                            : "bg-white border-gray-200",
                        ].join(" ")}
                    >
                        {imgSrc && (
                        <div className="relative bg-gray-100">
                            <div className="aspect-w-2 aspect-h-1">
                            <img
                                src={imgSrc}
                                alt={`Review ${idx + 1} image`}
                                className="object-cover w-full h-full"
                                loading="lazy"
                            />
                            </div>
                        </div>
                        )}

                        <div className={`p-3 sm:p-4 ${textOnly ? "pt-4" : ""}`}>
                        <h4 className="font-medium text-sm sm:text-base truncate">{review.user}</h4>
                        <div className="flex items-center mt-1">
                            {renderStars(review.rating)}
                            <span className="text-xs text-gray-500 ml-2">
                            ({review.rating?.toFixed?.(1) || review.rating})
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{formatDate(review.date)}</p>
                        <p className={["text-sm sm:text-base mt-2", textOnly ? "text-gray-800 italic" : "text-gray-700"].join(" ")}>
                            {review.comment}
                        </p>
                        </div>
                    </div>
                    </article>
                );
                })}
            </div>
            </section>
        );
    }

  return (
    <section
      className="relative"
      role="region"
      aria-roledescription="carousel"
      aria-label="Customer reviews slider"
      tabIndex={0}
      aria-live="off"
      onKeyDown={onKeyDown}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onTouchStart={onPointerDown}
      onTouchEnd={onPointerUp}
    >
      {/* Track */}
      <div className="overflow-hidden rounded-md">
        <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${page * 100}%)` }} >
        {reviews.map((review, idx) => {
            const imgSrc = getPrimaryImage(review);
            const textOnly = !imgSrc;

            return (
                <article
                    key={review.id}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`Slide ${idx + 1} of ${total}`}
                    className="px-1 shrink-0"            // prevent shrinking
                    style={{ flex: `0 0 ${slideWidth}`, maxWidth: slideWidth }}
                >
                    <div
                        className={[
                        "rounded-lg overflow-hidden shadow-sm border transition-colors",
                        textOnly
                            ? "bg-blue-50 border-blue-200 border-l-4 border-l-blue-500"
                            : "bg-white border-gray-200",
                        ].join(" ")}
                    >
                        {/* Image wrapper: only render when an image exists */}
                        {imgSrc && (
                        <div className="relative bg-gray-100">
                            <div className="aspect-w-2 aspect-h-1">
                            <img
                                src={imgSrc}
                                alt={`Review ${idx + 1} image`}
                                className="object-cover w-full h-full"
                                loading="lazy"
                            />
                            </div>
                        </div>
                        )}

                        {/* Text content */}
                        <div className={`p-3 sm:p-4 ${textOnly ? "pt-4" : ""}`}>
                            <h4 className="font-medium text-sm sm:text-base truncate">
                                {review.user}
                            </h4>

                            <div className="flex items-center mt-1">
                                {renderStars(review.rating)}
                                <span className="text-xs text-gray-500 ml-2">
                                ({review.rating?.toFixed?.(1) || review.rating})
                                </span>
                            </div>

                            <p className="text-xs text-gray-500 mt-1">{formatDate(review.date)}</p>

                            <p
                                className={[
                                "text-sm sm:text-base mt-2",
                                textOnly ? "text-gray-800 italic" : "text-gray-700",
                                ].join(" ")}
                            >
                                {review.comment}
                            </p>
                        </div>
                    </div>
                </article>
            );
        })}
        </div>
      </div>

      {/* Controls */}
      {pages > 1 && (
        <div className="mt-3 flex items-center justify-between">
          {/* <button
            onClick={prevPage}
            className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
            aria-label="Previous reviews"
          >
            Prev
          </button> */}

          <div role="group" aria-label="Slide selectors" className="flex gap-2">
            {Array.from({ length: pages }).map((_, i) => (
              <button
                key={i}
                onClick={() => goToPage(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-selected={i === page}
                className={`w-2.5 h-2.5 rounded-full ${i === page ? "bg-blue-600" : "bg-gray-300 hover:bg-gray-400"}`}
              />
            ))}
          </div>

          {/* <button
            onClick={nextPage}
            className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
            aria-label="Next reviews"
          >
            Next
          </button> */}
        </div>
      )}
    </section>
  );
};

// Add custom animations for product page
const customStyles = `

    @keyframes fadeIn {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }
  
  /* ... existing animations ... */
  
  /* Custom scrollbar for review text */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #DA9687;
    border-radius: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #c8847a;
  }
  
  /* Firefox scrollbar */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #DA9687 #f1f1f1;
  }

  @keyframes fadeIn {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }
  
  @keyframes pulse-border {
    0% { box-shadow: 0 0 0 0 rgba(6, 182, 212, 0.7); }
    70% { box-shadow: 0 0 0 10px rgba(6, 182, 212, 0); }
    100% { box-shadow: 0 0 0 0 rgba(6, 182, 212, 0); }
  }
  
  @keyframes highlight-glow {
    0%, 100% { box-shadow: 0 0 5px rgba(16, 185, 129, 0.2); }
    50% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.6); }
  }
  
  @keyframes slideIn {
    0% { transform: translateX(100%); opacity: 0; }
    100% { transform: translateX(0); opacity: 1; }
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.6s ease-out;
  }
  
  .animate-pulse-border {
    animation: pulse-border 2s infinite;
  }
  
  .animate-highlight-glow {
    animation: highlight-glow 1.5s infinite;
  }
  
  .animate-slideIn {
    animation: slideIn 0.5s ease-out;
  }
  
  .animate-spin {
    animation: spin 1s linear infinite;
  }
  
  /* Smooth carousel transitions */
  .carousel-container {
    scroll-behavior: smooth;
  }
  
  .product-card-hover {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .product-card-hover:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

// Helper to find by slug if the page is visited directly (fallback)
const findBySlug = (s) => translations?.products?.product?.find((p) => p?.slug === s);

const ProductPage = ({ product: productProp }) => {
    const { slug } = useParams();
    const { addToCart } = useCart();
    const navigate = useNavigate();
       // Prefer product prop from route; else resolve by slug param
  const product = useMemo(() => {
    if (productProp) return productProp;
    if (slug) return findBySlug(slug);
    return null;
  }, [productProp, slug]);

    const [showNotification, setShowNotification] = useState(false);
    const [showPaymentBanner, setShowPaymentBanner] = useState(true);
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const [checkoutError, setCheckoutError] = useState(null);
    const [activeTab, setActiveTab] = useState('reviews');
    const [activeImage, setActiveImage] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [zoom, setZoom] = useState(false);
    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [recentlyViewed, setRecentlyViewed] = useState([]);
    const [currentSlide, setCurrentSlide] = useState(0);

    // Carousel functions for recently viewed products
    const productsPerSlide = 4;
    const totalSlides = Math.ceil(recentlyViewed.length / productsPerSlide);
    const { name, cost, photo = {}, description, fullDescription, reviews = [], slug: productSlug } = product;
    const price = Number(cost || 0).toFixed(2);
    const img = photo?.image1 || photo?.image || '';
    
    // Check if the product has free accessories directly from the product data
    const hasFreeAccessories = product?.freeAccessories && product.freeAccessories.length > 0;
    // Use the accessories from product data if available
    const freeAccessories = product?.freeAccessories || [];
    // Compute site URL (can be overridden via env at build time)
    const SITE_URL =  'https://sacredrelm.com';
    // Use product-specific reviews from data
    const productReviews = product?.reviews || [];
    const averageRating = React.useMemo(
    () => calculateAverageRating(productReviews),
    [productReviews]
    );

    const ratingDistribution = React.useMemo(
    () => calculateRatingDistribution(productReviews),
    [productReviews]
    );
    const [timeRemaining, setTimeRemaining] = useState(
        () => getCountdownTimeForProduct(Number(product?.id))
    );

    useEffect(() => {
    setTimeRemaining(getCountdownTimeForProduct(Number(product?.id)));
    }, [product?.id]);

    
    // Get different countdown times based on product ID
    function getCountdownTimeForProduct(productId) {
    const timePatterns = {
        1: { hours: 2, minutes: 37, seconds: 42 },
        2: { hours: 6, minutes: 14, seconds: 29 },
        3: { hours: 3, minutes: 58, seconds: 15 },
        4: { hours: 1, minutes: 12, seconds: 33 },
        5: { hours: 5, minutes: 42, seconds: 21 },
        6: { hours: 7, minutes: 18, seconds: 46 },
        7: { hours: 2, minutes: 23, seconds: 11 },
        8: { hours: 4, minutes: 50, seconds: 37 },
        9: { hours: 8, minutes: 5, seconds: 52 },
        10:{ hours: 1, minutes: 49, seconds: 18 },
        11:{ hours: 6, minutes: 32, seconds: 45 },
        12:{ hours: 3, minutes: 15, seconds: 29 },
        default: { hours: 4, minutes: 59, seconds: 59 },
    };
    return timePatterns[productId] || timePatterns.default;
    }
    
    // SEO-friendly data
    const productSchema = {
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": product?.name || "",
        "image": product?.photo?.image1 || "",
        "sku": `SKU-${product?.id || "0000"}`,
        "brand": {
            "@type": "Sacredrelm",
            "name": "Sacredrelm"
        },
        "offers": {
            "@type": "Offer",
            "url": product ? `${SITE_URL}${createProductUrl(product.id, product.name)}` : window.location.href,
            "priceCurrency": "INR",
            "price": product?.cost || 0,
            "availability": "https://schema.org/InStock",
            "itemCondition": "https://schema.org/NewCondition"
        }
    };

    useEffect(() => {
        // Set the first image as active when product loads
        if (product && product.photo && product.photo.image1) {
            setActiveImage(product.photo.image1);
        }
        
        // Reset timer when product changes
        setTimeRemaining(getCountdownTimeForProduct(Number(product?.id)));
        
        // Scroll to top when product changes
        window.scrollTo(0, 0);
        
        // Handle recently viewed products storage
        if (product) {
            const allProducts = translations.products.product;
            const otherProducts = allProducts.filter(p => p.id !== product.id);
            setRecentlyViewed(otherProducts);
        }

        // Hide payment banner after scrolling
        const handleScroll = () => {
            if (window.scrollY > 300) {
                setShowPaymentBanner(false);
            } else {
                setShowPaymentBanner(true);
            }
        };
        
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [product]);

    useEffect(() => {
        // Countdown timer for urgency
        const countdownInterval = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev.seconds > 0) {
                    return { ...prev, seconds: prev.seconds - 1 };
                } else if (prev.minutes > 0) {
                    return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
                } else if (prev.hours > 0) {
                    return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
                } else {
                    // Reset timer when it reaches 0
                    return { hours: 4, minutes: 59, seconds: 59 };
                }
            });
        }, 1000);
        return () => {
            clearInterval(countdownInterval);
        };
    }, [product]);

    const handleAddToCart = () => {
        addToCart({ id: product.id, name: product.name, cost: product.cost, photo: product.photo, cartItemId: `${product.id}-${Date.now()}` });
    };

    const handleBuyNow = async () => {
        setIsCheckoutLoading(true);
        setCheckoutError(null);

        try {
            // Get existing cart items
            const existingCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];

            // Create current product item
            const currentProductItem = {
                name: product.name,
                quantity: quantity,
                price: Number(product.cost),
                cost: Number(product.cost),
            };

            // Process existing items
            const processedCartItems = existingCartItems.map(item => ({
                name: item.name,
                quantity: 1,
                price: Number(item.cost),
                cost: Number(item.cost),
            }));

            // Combine all items
            const allItems = [...processedCartItems, currentProductItem];

            // Calculate total
            const totalAmount = allItems.reduce((total, item) => {
                return total + (Number(item.price) || Number(item.cost) || 0) * (item.quantity || 1);
            }, 0);

            // Navigate to billing page
            navigate('/billing', {
                state: {
                    items: allItems,
                    totalAmount: totalAmount,
                    quantity: allItems.length,
                    paymentMode: 'online',
                },
            });
        } catch (error) {
            setCheckoutError(error.message);
            console.error('Checkout failed:', error);
        } finally {
            setIsCheckoutLoading(false);
        }
    };

    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating - fullStars >= 0.5;
        
        for (let i = 1; i <= 5; i++) {
            if (i <= fullStars) {
                stars.push(<FaStar key={i} className="text-yellow-400" />);
            } else if (i === fullStars + 1 && hasHalfStar) {
                stars.push(<FaStarHalfAlt key={i} className="text-yellow-400" />);
            } else {
                stars.push(<FaRegStar key={i} className="text-yellow-400" />);
            }
        }
        
        return <div className="flex">{stars}</div>;
    };
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };
        
    // Calculate average rating from product reviews
    function calculateAverageRating(productReviews = []) {
    if (!productReviews.length) return 4.5; // default if no reviews
    const total = productReviews.reduce((sum, review) => sum + Number(review.rating || 0), 0);
    return Number((total / productReviews.length).toFixed(1));
    }

    // Calculate rating distribution percentages
    function calculateRatingDistribution(productReviews = []) {
        if (!productReviews.length) return { 5: 70, 4: 20, 3: 5, 2: 3, 1: 2 }; // default shape
        const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        productReviews.forEach((review) => {
            const rating = Math.floor(Number(review.rating || 0));
            if (distribution[rating] != null) distribution[rating] += 1;
        });
        const total = productReviews.length;
        Object.keys(distribution).forEach((k) => {
            distribution[k] = Math.round((distribution[k] / total) * 100);
        });
        return distribution;
    }
    
    // Enhance the countdown timer to show different messages based on time remaining
    const getUrgencyMessage = () => {
        const totalSeconds = timeRemaining.hours * 3600 + timeRemaining.minutes * 60 + timeRemaining.seconds;
        
        if (hasFreeAccessories) {
            if (totalSeconds < 3600) {
                return 'HURRY! Free accessories offer ends soon!';
            } else {
                return 'Order now to receive free accessories!';
            }
        } else {
            if (totalSeconds < 3600) {
                return 'FINAL chance! Price increases soon!';
            } else {
                return 'Limited time offer! Hurry before price goes up!';
            }
        }
    };
    
    const nextSlide = useCallback(() => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, [totalSlides]);
    
    // Auto-slide functionality
    useEffect(() => {
        if (recentlyViewed.length > productsPerSlide) {
            const slideInterval = setInterval(() => {
                nextSlide();
            }, 4000);

            return () => clearInterval(slideInterval);
        }
    }, [recentlyViewed.length, nextSlide]);

    // Don't render if product not found
    if (!product) {
        return <div>Product not found</div>;
    }
    
    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    };
    
    const goToSlide = (slideIndex) => {
        setCurrentSlide(slideIndex);
    };

    const highlightBraces = (text) => {
        if (!text) return null;
        const parts = [];
        const regex = /{([^}]+)}/g;
        let last = 0, m;
        while ((m = regex.exec(text)) !== null) {
            if (m.index > last) parts.push(text.slice(last, m.index));
            parts.push(<strong key={parts.length}>{m[1]}</strong>);
            last = regex.lastIndex;
        }
        if (last < text.length) parts.push(text.slice(last));
        return parts;
    };

    const renderFullDescription = (fullDescription) => {
        if (!fullDescription) return null;
        const lines = fullDescription.split('\n');

        const out = [];
        let listType = null; // 'ul' | 'ol'
        let items = [];

        const flushList = () => {
            if (!listType || items.length === 0) return;
            if (listType === 'ul') {
            out.push(
                <ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1">
                {items.map((it, i) => <li key={i}>{it}</li>)}
                </ul>
            );
            } else {
            out.push(
                <ol key={`ol-${out.length}`} className="list-decimal pl-5 space-y-1">
                {items.map((it, i) => <li key={i}>{it}</li>)}
                </ol>
            );
            }
            listType = null;
            items = [];
        };

        for (let i = 0; i < lines.length; i++) {
            const raw = (lines[i] || '').trim();
            if (!raw) { flushList(); continue; }

            // Heading lines starting with "##" or "## "
            if (raw.startsWith('##')) {
            flushList();
            const title = raw.replace(/^##\s*/, '');
            out.push(
                <h3 key={`h-${out.length}`} className="text-lg sm:text-xl font-semibold text-gray-800 mt-3">
                {highlightBraces(title)}
                </h3>
            );
            continue;
            }

            // Ordered list: "1. Something"
            const olMatch = raw.match(/^\d+\.\s+(.*)$/);
            if (olMatch) {
            if (listType !== 'ol') { flushList(); listType = 'ol'; }
            items.push(<span>{highlightBraces(olMatch[1])}</span>);
            continue;
            }

            // Unordered list: "- Something"
            const ulMatch = raw.match(/^-\s+(.*)$/);
            if (ulMatch) {
            if (listType !== 'ul') { flushList(); listType = 'ul'; }
            items.push(<span>{highlightBraces(ulMatch[1])}</span>);
            continue;
            }

            // Plain paragraph
            flushList();
            out.push(
            <p key={`p-${out.length}`} className="break-words overflow-wrap-anywhere">
                {highlightBraces(raw)}
            </p>
            );
        }
        flushList();
        return out;
    };

    if (!product) return <Navigate to="/" replace />;
    if (!product) return <div>Product not found</div>;


    return (
        <div>
            <style>{customStyles}</style>
            {/* SEO Optimization */}
            <Helmet>
                <title>{product.metaTitle ? `${product.metaTitle} | Sacred Relm` : `${product.name} | Sacred Relm`}</title>
                <meta name="description" content={product.metaDescription || `Buy ${product.name} - ${product.description?.substring(0, 150) || 'Premium spiritual product at the best price'}`} />
                <meta name="keywords" content={
                    product.focusKeyword && product.tags 
                        ? `${product.focusKeyword}, ${product.tags.join(', ')}, ${product.name}, ${product.category}, spiritual items, sacred, buy online`
                        : `${product.name}, ${product.category}, spiritual items, sacred, buy online`
                } />
                <link rel="canonical" href={`${SITE_URL}${createProductUrl(product.id, product.name)}`} />
                <meta property="og:title" content={product.metaTitle ? `${product.metaTitle} | Sacred Relm` : `${product.name} | Sacred Relm`} />
                <meta property="og:description" content={product.metaDescription || product.description?.substring(0, 150) || 'Premium spiritual product at the best price'} />
                <meta property="og:image" content={`${SITE_URL}${product.photo.image1}`} />
                <meta property="og:url" content={`${SITE_URL}${createProductUrl(product.id, product.name)}`} />
                <meta property="og:type" content="product" />
                <meta name="twitter:card" content="summary_large_image" />
                <script type="application/ld+json">
                    {JSON.stringify(productSchema)}
                </script>
            </Helmet>
             <BusinessSchema pageType="product" />
            <div>
                <PageHeader title={product.name} subtitle="Home > Store > Product" />
                
                {/* Notification */}
                {showNotification && (
                    <div className="fixed top-16 sm:top-24 left-0 sm:left-1/2 transform sm:-translate-x-1/2 z-50 w-full sm:w-[90%] sm:max-w-md px-4 sm:px-0">
                        <div className="bg-green-500 text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg flex items-center justify-center">
                            <FaCheck className="mr-2" />
                            <span>Added to cart successfully!</span>
                        </div>
                    </div>
                )}

                {/* Checkout Error Notification */}
                {checkoutError && (
                    <div className="fixed top-16 sm:top-24 left-0 sm:left-1/2 transform sm:-translate-x-1/2 z-50 w-full sm:w-[90%] sm:max-w-md px-4 sm:px-0">
                        <div className="bg-red-500 text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg flex items-center justify-between">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <span className="text-sm">{checkoutError}</span>
                            </div>
                            <button
                                onClick={() => setCheckoutError(null)}
                                className="ml-2 text-white hover:text-gray-200"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}

                <div className="bg-[#faebd7] mx-auto px-3 sm:px-4 py-3 sm:py-8">
                    <div className="">
                        {/* Product Top Section */}
                        <div className="bg-[#faebd7] rounded-xl sm:rounded-2xl shadow-sm overflow-hidden">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                                {/* Product Images - Left Side */}
                                <div className="p-3 sm:p-6 bg-[#faebd7] border-b md:border-r md:border-b-0 border-gray-100 relative">
                                    {/* Main Image */}
                                    <div 
                                        className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg ${zoom ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
                                        onClick={() => setZoom(!zoom)}
                                    >
                                        <img
                                            src={activeImage}
                                            alt={product.name}
                                            className={`w-full h-[250px] sm:h-[350px] md:h-[500px] object-contain transition-all duration-300 ${zoom ? 'scale-150' : 'scale-100'}`}
                                            loading="eager"
                                            title={product.name}
                                        />
                                    </div>
                                    
                                    {/* Thumbnail Gallery */}
                                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-1 sm:gap-4 mt-3 sm:mt-6">
                                        {Object.values(product.photo).map((image, index) => (
                                            <div
                                                key={index}
                                                className={`border-2 rounded-lg overflow-hidden cursor-pointer transition-all hover:shadow-md ${activeImage === image ? 'border-blue-500 shadow-sm' : 'border-transparent'}`}
                                                onClick={() => setActiveImage(image)}
                                            >
                                                <img
                                                    src={image}
                                                    alt={`${product.name} view ${index + 1}`}
                                                    className="w-full h-full object-contain"
                                                    loading="lazy"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Product Info - Right Side */}
                                <div className="p-3 sm:p-8 space-y-3 sm:space-y-6">
                                    {/* Product Title & Rating */}
                                    <div className="space-y-2">
                                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{product.name}</h1>
                                        <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                                            {product.description || 'A premium quality product crafted with attention to detail and designed for everyday use.'}
                                        </p>
                                        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                                            <div className="flex items-center gap-2">
                                                {renderStars(4.5)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Price Section */}
                                    <div className="bg-[#faebd7] p-3 sm:p-4 rounded-lg">
                                        <div className="flex items-center space-x-2 sm:space-x-4">
                                            <p className="text-2xl sm:text-3xl font-bold text-blue-600">₹{product.cost.toLocaleString()}</p>
                                            <p className="text-sm sm:text-lg text-gray-500 line-through">₹{Math.floor(product.cost * 1.2).toLocaleString()}</p>
                                            <span className="bg-red-100 text-red-800 px-2 py-1 rounded-md text-xs sm:text-sm font-medium">20% OFF</span>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">* Inclusive of all taxes</p>
                                    </div>

                                    {/* Free Accessories section - keeping your existing code */}
                                    {hasFreeAccessories && (
                                        <div className="bg-gradient-to-r from-orange-50 to-blue-50 border-2 border-dashed border-orange-200 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3 relative overflow-hidden transform transition-all hover:scale-[1.01] animate-fadeIn">
                                            <div className="absolute -right-12 -top-4 bg-red-500 text-white px-12 py-1 transform rotate-45 shadow-md">
                                                <span className="text-xs font-bold">LIMITED TIME</span>
                                            </div>
                                            
                                            <h3 className="flex items-center text-orange-700 font-bold text-lg sm:text-xl">
                                                <FaGift className="mr-2 text-orange-600" /> 
                                                Free Premium Accessories
                                            </h3>
                                            
                                            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-400 rounded-lg p-3 sm:p-4 relative overflow-hidden shadow-sm">
                                                <div className="absolute top-0 right-0 h-full w-1/3 bg-gradient-to-l from-yellow-100 to-transparent opacity-70"></div>
                                                
                                                <div className="flex items-start space-x-3">
                                                    <div className="p-2 bg-amber-100 rounded-full">
                                                        <FaInfoCircle className="text-amber-500 text-lg animate-pulse" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-amber-800 text-sm sm:text-base mb-1">
                                                            ONLINE PAYMENT EXCLUSIVE
                                                        </p>
                                                        <p className="text-amber-700 text-sm">
                                                            These premium accessories (worth <span className="font-bold">₹{Math.floor(product.cost * 0.2)}</span>) are <span className="underline font-bold">only included with online payment methods</span>. Not available with cash on delivery.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 mt-3">
                                                {freeAccessories.map((item, idx) => (
                                                    <div key={idx} className="flex items-center bg-[#faebd7] p-2 sm:p-3 rounded-lg shadow-md border border-orange-100 hover:border-orange-300 transition-all">
                                                        <div className="relative">
                                                            <img 
                                                                src={item.image} 
                                                                alt={item.name} 
                                                                className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-md"
                                                                loading="lazy"
                                                            />
                                                            <div className="absolute -bottom-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                                                <FaCheck size={10} />
                                                            </div>
                                                        </div>
                                                        <div className="ml-3">
                                                            <h4 className="text-sm sm:text-base font-medium text-gray-800">{item.name}</h4>
                                                            {/* <p className="text-xs text-gray-600 hidden sm:block">{item.description}</p> */}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            
                                            <div className="flex justify-center mt-1">
                                                <span className="text-sm text-gray-500 italic">
                                                    Tap "Buy Now" to secure these free accessories today!
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Short description */}
                                    {/* <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                                        {product.description || 'A premium quality product crafted with attention to detail and designed for everyday use.'}
                                    </p> */}

                                    {/* Quantity Selector */}
                                    <div className="flex items-center space-x-4">
                                        <span className="text-gray-700 text-sm sm:text-base">Quantity:</span>
                                        <div className="flex items-center border border-gray-300 rounded-md">
                                            <button 
                                                onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                                                className="px-3 py-2 text-lg border-r hover:bg-gray-100 transition"
                                                aria-label="Decrease quantity"
                                            >
                                                -
                                            </button>
                                            <span className="px-4 py-2 min-w-[40px] text-center">{quantity}</span>
                                            <button 
                                                onClick={() => setQuantity(quantity + 1)}
                                                className="px-3 py-2 text-lg border-l hover:bg-gray-100 transition"
                                                aria-label="Increase quantity"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    {/* Enhanced Action Buttons with Shopify Integration */}
                                    <div className="space-y-3 pt-2">
                                        {/* Countdown Timer */}
                                        <div className="bg-gradient-to-r from-red-50 via-red-100 to-red-50 border border-red-200 rounded-lg p-2 sm:p-3 text-center animate-pulse-border">
                                            <p className="text-red-700 font-medium text-xs sm:text-sm">Special offer ends in:</p>
                                            <div className="flex justify-center items-center space-x-1 sm:space-x-2 mt-1">
                                                <div className="bg-red-600 text-white px-1 sm:px-2 py-1 rounded text-xs sm:text-base">
                                                    <span className="font-bold">{String(timeRemaining.hours).padStart(2, '0')}</span>
                                                </div>
                                                <span className="font-bold text-red-600">:</span>
                                                <div className="bg-red-600 text-white px-1 sm:px-2 py-1 rounded text-xs sm:text-base">
                                                    <span className="font-bold">{String(timeRemaining.minutes).padStart(2, '0')}</span>
                                                </div>
                                                <span className="font-bold text-red-600">:</span>
                                                <div className="bg-red-600 text-white px-1 sm:px-2 py-1 rounded text-xs sm:text-base">
                                                    <span className="font-bold">{String(timeRemaining.seconds).padStart(2, '0')}</span>
                                                </div>
                                            </div>
                                            <p className="text-xxs sm:text-xs text-red-600 mt-1">
                                                {getUrgencyMessage()}
                                            </p>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                                            <button
                                                onClick={handleAddToCart}
                                                className="bg-[#DA9687] text-white px-4 sm:px-8 py-3 rounded-lg hover:bg-opacity-90 hover:shadow-md transition-all flex-1 flex justify-center items-center text-sm sm:text-base"
                                                disabled={isCheckoutLoading}
                                            >
                                                <FaShoppingCart className="mr-1 sm:mr-2" />
                                                Add to Cart
                                            </button>
                                            
                                            {/* Enhanced Buy Now Button with Shopify Integration */}
                                            <button
                                                onClick={handleBuyNow}
                                                disabled={isCheckoutLoading}
                                                className={`relative overflow-hidden ${hasFreeAccessories 
                                                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg' 
                                                    : 'bg-blue-600 hover:bg-blue-700'} 
                                                    text-white px-4 sm:px-8 py-3 rounded-lg transition-all flex-1 flex justify-center items-center group text-sm sm:text-base font-bold
                                                    ${isCheckoutLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                                            >
                                                {/* Loading Spinner */}
                                                {isCheckoutLoading && (
                                                    <div className="absolute inset-0 bg-blue-700 bg-opacity-50 flex items-center justify-center">
                                                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                                    </div>
                                                )}

                                                {/* Pulsing border effect */}
                                                <span className="absolute inset-0 border-2 border-white rounded-lg opacity-0 group-hover:opacity-30 animate-pulse"></span>
                                                
                                                {/* Shimmer effect */}
                                                {hasFreeAccessories && (
                                                    <span className="absolute inset-0 w-full h-full">
                                                        <span className="absolute top-0 left-0 w-full h-full bg-white opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
                                                    </span>
                                                )}
                                                
                                                {/* Icon and text */}
                                                <div className="flex items-center">
                                                    <FaShoppingCart className="mr-1 sm:mr-2" />
                                                    <span className="font-bold tracking-wider">
                                                        {isCheckoutLoading ? 'PROCESSING...' : 'BUY NOW'}
                                                    </span>
                                                    {hasFreeAccessories && <FaGift className="ml-1 sm:ml-2 animate-pulse" />}
                                                </div>
                                                
                                                {/* "Act now" label */}
                                                {!isCheckoutLoading && (
                                                    <div className="absolute -bottom-1 -right-1 transform translate-x-1/4 translate-y-1/4 bg-yellow-400 text-blue-800 text-[8px] sm:text-[9px] uppercase font-extrabold px-1 sm:px-2 py-0.5 rounded-full tracking-wide rotate-12 shadow-sm">
                                                        Act now!
                                                    </div>
                                                )}
                                            </button>
                                        </div>
                                        <div className='flex gap-5'>
                                            <span className="text-green-600 flex items-center gap-1">
                                                <FaCheck size={14} />
                                                In Stock
                                            </span>
                                            <span className="hidden sm:inline text-gray-500">|</span>
                                            <span className="text-blue-600 flex items-center gap-1">
                                                <FaTruck size={14} />
                                                Fast Shipping
                                            </span>
                                        </div>

                                        {/* Amazon Button */}
                                        {product.amazon && (
                                            <div id="amazon" className="pt-2">
                                                <a 
                                                    id="amazon" 
                                                    href={product.amazon} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    className="amazon button w-full text-black px-4 sm:px-8 rounded-lg transition-all flex justify-center items-start text-sm sm:text-base font-bold shadow-lg transform hover:scale-105"
                                                >
                                                    <span id="amazon" className="tracking-wider font-bold pt-[4px]">ALSO AVAILABLE ON </span>
                                                    <img id="amazon" src={amazon} className="w-20" alt="Amazon" />
                                                </a>
                                                <p className="text-xs text-gray-500 text-center mt-1">
                                                    Also available on Amazon with fast delivery
                                                </p>
                                            </div>
                                        )}
                                        
                                    </div>
                                    
                                </div>
                            </div>
                        </div>
                        <div className="text-gray-700 leading-relaxed text-sm sm:text-base space-y-3 w-full">
                            <h1 className='text-3xl font-bold'>Product Description</h1>
                            
                            {renderFullDescription(product.fullDescription)}
                        </div>

                        <div className='flex flex-col md:flex-row lg:flex-row'>                        
                            {/* Benefits Section */}
                            {product.benefits && product.benefits.length > 0 && (
                                <div className="bg-[#faebd7] rounded-lg shadow-sm overflow-hidden mt-3 sm:mt-4 md:mt-6 p-3 sm:p-4 md:p-6 w-full">
                                    <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4 flex items-center">
                                        <FaCheck className="mr-1.5 sm:mr-2 text-[#DA9687] " size={14} />
                                        <span>Key Benefits</span>
                                    </h2>
                                    <div className="grid grid-cols-1 gap-2 sm:gap-2.5 md:gap-3 w-full">
                                        {product.benefits.map((benefit, index) => (
                                            <div key={index} className="flex items-start gap-2 sm:gap-2.5 md:gap-3 bg-white bg-opacity-50 p-2 sm:p-2.5 md:p-3 rounded-md sm:rounded-lg border border-gray-200 w-full">
                                                <FaCheck className="text-[#DA9687] mt-0.5 sm:mt-1" size={12} />
                                                <p className="text-gray-700 text-xs sm:text-sm md:text-base break-words leading-relaxed">{benefit}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Placement Guidelines Section */}
                            {(product.placement || product.howToUse) && (
                                <div className="bg-[#faebd7] rounded-xl sm:rounded-2xl shadow-sm overflow-hidden mt-4 sm:mt-6 p-4 sm:p-6 w-full">
                                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 flex items-center">
                                        <FaInfoCircle className="mr-2 text-[#DA9687] " />
                                        {product.placement ? 'Placement Guidelines' : 'How to Use'}
                                    </h2>
                                    <div className="space-y-3 w-full">
                                        {(product.placement || product.howToUse).map((item, index) => (
                                            <div key={index} className="flex items-start gap-3 bg-white bg-opacity-50 p-3 rounded-lg border border-gray-200 w-full">
                                                <span className="text-[#DA9687] font-bold text-lg ">{index + 1}.</span>
                                                <p className="text-gray-700 text-sm sm:text-base flex-1 break-words overflow-wrap-anywhere">{item}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* FAQ Section */}
                        {product.faqs && product.faqs.length > 0 && (
                            <div className="bg-[#faebd7] rounded-xl sm:rounded-2xl shadow-sm overflow-hidden mt-4 sm:mt-6 p-4 sm:p-6 w-full">
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 flex items-center">
                                    <FaQuestionCircle className="mr-2 text-[#DA9687] " />
                                    Frequently Asked Questions
                                </h2>
                                <div className="space-y-4 w-full">
                                    {product.faqs.map((faq, index) => (
                                        <div key={index} className="bg-white bg-opacity-50 p-4 rounded-lg border border-gray-200 w-full">
                                            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 flex items-start">
                                                <span className="text-[#DA9687] mr-2 ">Q{index + 1}.</span>
                                                <span className="break-words overflow-wrap-anywhere flex-1">{faq.question}</span>
                                            </h3>
                                            <p className="text-gray-700 text-sm sm:text-base pl-8 break-words overflow-wrap-anywhere">
                                                {faq.answer}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeTab === 'reviews' && (
                            <div>
                                <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Customer Reviews</h2>

                                {/* Rating summary + distribution */}
                                <div className="flex flex-col md:flex-row md:items-center gap-4 sm:gap-8 mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-gray-200">
                                <div className="text-center md:border-r md:border-gray-200 md:pr-8">
                                    <div className="text-4xl sm:text-5xl font-bold text-gray-900">{averageRating}</div>
                                    <div className="flex justify-center mt-2">
                                    {renderStars(parseFloat(averageRating))}
                                    </div>
                                    <div className="text-xs sm:text-sm text-gray-500 mt-1">Based on {productReviews.length} reviews</div>
                                </div>

                                <div className="flex-1">
                                    <div className="flex items-center mb-1">
                                    <span className="w-9 sm:w-12 text-xs sm:text-sm">5 star</span>
                                    <div className="flex-1 h-2 mx-2 bg-gray-200 rounded-full">
                                        <div className="h-2 bg-yellow-400 rounded-full" style={{ width: `${ratingDistribution[5]}%` }} />
                                    </div>
                                    <span className="w-9 sm:w-12 text-right text-xs sm:text-sm">{ratingDistribution[5]}%</span>
                                    </div>
                                    <div className="flex items-center mb-1">
                                    <span className="w-9 sm:w-12 text-xs sm:text-sm">4 star</span>
                                    <div className="flex-1 h-2 mx-2 bg-gray-200 rounded-full">
                                        <div className="h-2 bg-yellow-400 rounded-full" style={{ width: `${ratingDistribution[4]}%` }} />
                                    </div>
                                    <span className="w-9 sm:w-12 text-right text-xs sm:text-sm">{ratingDistribution[4]}%</span>
                                    </div>
                                    <div className="flex items-center mb-1">
                                    <span className="w-9 sm:w-12 text-xs sm:text-sm">3 star</span>
                                    <div className="flex-1 h-2 mx-2 bg-gray-200 rounded-full">
                                        <div className="h-2 bg-yellow-400 rounded-full" style={{ width: `${ratingDistribution[3]}%` }} />
                                    </div>
                                    <span className="w-9 sm:w-12 text-right text-xs sm:text-sm">{ratingDistribution[3]}%</span>
                                    </div>
                                    <div className="flex items-center mb-1">
                                    <span className="w-9 sm:w-12 text-xs sm:text-sm">2 star</span>
                                    <div className="flex-1 h-2 mx-2 bg-gray-200 rounded-full">
                                        <div className="h-2 bg-yellow-400 rounded-full" style={{ width: `${ratingDistribution[2]}%` }} />
                                    </div>
                                    <span className="w-9 sm:w-12 text-right text-xs sm:text-sm">{ratingDistribution[2]}%</span>
                                    </div>
                                    <div className="flex items-center mb-1">
                                    <span className="w-9 sm:w-12 text-xs sm:text-sm">1 star</span>
                                    <div className="flex-1 h-2 mx-2 bg-gray-200 rounded-full">
                                        <div className="h-2 bg-yellow-400 rounded-full" style={{ width: `${ratingDistribution[1]}%` }} />
                                    </div>
                                    <span className="w-9 sm:w-12 text-right text-xs sm:text-sm">{ratingDistribution[1]}%</span>
                                    </div>
                                </div>
                                </div>

                                {/* Replace the old mapped list with the slider below */}
                                {productReviews?.length > 0 && (
                                <ReviewsSlider
                                    reviews={productReviews}
                                    renderStars={renderStars}
                                    formatDate={formatDate}
                                />
                                )}
                            </div>
                        )}


                        {recentlyViewed.length > 0 && (
                            <div className="mb-6 sm:mb-12">
                                <div className="flex justify-between items-center mb-3 sm:mb-6">
                                    <h2 className="text-lg sm:text-2xl font-bold">All Products</h2>
                                    {totalSlides > 1 && (
                                        <div className="flex items-center gap-2">
                                            <button 
                                                onClick={prevSlide}
                                                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                                                aria-label="Previous products"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                </svg>
                                            </button>
                                            <button 
                                                onClick={nextSlide}
                                                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                                                aria-label="Next products"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Carousel Container */}
                                <div className="relative overflow-hidden">
                                    <div 
                                        className="flex transition-transform duration-500 ease-in-out"
                                        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                                    >
                                        {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                                            <div key={slideIndex} className="min-w-full">
                                                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-6">
                                                    {recentlyViewed
                                                        .slice(slideIndex * productsPerSlide, (slideIndex + 1) * productsPerSlide)
                                                        .map((recentProduct) => (
                                                        <div key={recentProduct.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden product-card-hover group animate-slideIn">
                                                            <div className="aspect-w-2 bg-gray-200 relative overflow-hidden">
                                                                <img 
                                                                    src={recentProduct.photo.image1} 
                                                                    alt={recentProduct.name} 
                                                                    className="w-full sm:h-48 md:h-72 lg:h-72  transform group-hover:scale-110 transition duration-500"
                                                                    loading="lazy"
                                                                />
                                                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                                                    <button 
                                                                        onClick={() => navigate(`/product/${recentProduct.id}`)}
                                                                        className="bg-white text-gray-900 px-3 py-1 sm:px-4 sm:py-2 rounded-lg transform scale-95 group-hover:scale-100 transition-transform font-medium text-xs sm:text-sm shadow-lg"
                                                                    >
                                                                        View Details
                                                                    </button>
                                                                </div>
                                                                {recentProduct.isNew && (
                                                                    <div className="absolute top-2 left-2">
                                                                        <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                                                                            NEW
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="p-2 sm:p-4">
                                                                <h3 className="font-medium truncate group-hover:text-blue-600 transition text-xs sm:text-base line-clamp-2">{recentProduct.name}</h3>
                                                                <div className="flex items-center mt-1 scale-75 sm:scale-100 origin-left">
                                                                    {renderStars(recentProduct.rating || 4)}
                                                                    <span className="text-xs text-gray-500 ml-1">({recentProduct.rating || 4.0})</span>
                                                                </div>
                                                                <div className="mt-2 flex items-center justify-between">
                                                                    <div>
                                                                        <div className="font-bold text-xs sm:text-base text-blue-600">₹{recentProduct.cost.toLocaleString()}</div>
                                                                        <div className="text-xs text-gray-500 line-through">₹{Math.floor(recentProduct.cost * 1.2).toLocaleString()}</div>
                                                                    </div>
                                                                    <div className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium">
                                                                        20% OFF
                                                                    </div>
                                                                </div>
                                                                <button 
                                                                    onClick={() => navigate(`/product/${recentProduct.id}`)} 
                                                                    className="mt-2 sm:mt-3 w-full bg-gradient-to-r from-[#DA9687] to-[#c8847a] text-white py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium hover:from-[#c8847a] hover:to-[#b67469] transition-all duration-300 transform hover:scale-105 shadow-md"
                                                                >
                                                                    Shop Now
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Pagination Dots */}
                                {totalSlides > 1 && (
                                    <div className="flex justify-center mt-4 gap-2">
                                        {Array.from({ length: totalSlides }).map((_, index) => (
                                            <button
                                                key={index}
                                                onClick={() => goToSlide(index)}
                                                className={`w-3 h-3 rounded-full transition-colors ${
                                                    index === currentSlide 
                                                        ? 'bg-blue-600' 
                                                        : 'bg-gray-300 hover:bg-gray-400'
                                                }`}
                                                aria-label={`Go to slide ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                )}
                                
                                {/* Progress bar for auto-slide */}
                                {totalSlides > 1 && (
                                    <div className="mt-3 w-full bg-gray-200 rounded-full h-1">
                                        <div 
                                            className="bg-blue-600 h-1 rounded-full transition-all duration-1000 ease-linear"
                                            style={{ width: `${((currentSlide + 1) / totalSlides) * 100}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ProductPage;




// import React, { useState, useEffect, useCallback, useRef, useMemo  } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import { useCart } from '../components/CartContext';
// import { Helmet } from 'react-helmet';
// import { createProductUrl } from '../utils/urlSlugs';
// import { FaStar, FaStarHalfAlt, FaRegStar, FaShippingFast, FaCheck, FaExchangeAlt, FaShare, FaFacebook, FaTwitter, FaInstagram, FaYoutube, FaGift, FaHeart, FaClock, FaShieldAlt, FaTruck, FaInfoCircle, FaShoppingCart } from 'react-icons/fa';
// import translations from '../utils/data';
// import PageHeader from './Other';
// import amazon from '../assets/amazon.webp';
// import { FaQuestionCircle } from 'react-icons/fa';
// import BusinessSchema from './BusinessSchema';

// const ReviewsSlider = ({ reviews, renderStars, formatDate }) => {
//   const total = reviews?.length || 0;
//   const isCompact = total > 0 && total <= 5;
//   const [page, setPage] = useState(0);             // page index (0..pages-1)
//   const [spv, setSpv] = useState(1);               // slides per view (responsive)
//   const [paused, setPaused] = useState(false);

//   const startXRef = useRef(null);
//   const isDraggingRef = useRef(false);

//   // Responsive slides-per-view similar to product card rows
// const computeSPV = useCallback(() => {
//   const w = window.innerWidth;
//   if (w >= 1440) return 4;     // xl+: show 4 wide
//   if (w >= 1024) return 3;     // lg: show 3 wide
//   if (w >= 740)  return 2;     // sm/md: show 2 wide
//   return 1;                    // xs: show 1
// }, []);

//   useEffect(() => {
//     const apply = () => setSpv(computeSPV());
//     apply();
//     window.addEventListener("resize", apply);
//     return () => window.removeEventListener("resize", apply);
//   }, [computeSPV]);

//   const pages = Math.max(1, Math.ceil(total / spv));

//   const nextPage = () => setPage((p) => (p + 1) % pages);
//   const prevPage = () => setPage((p) => (p - 1 + pages) % pages);
//   const goToPage = (i) => setPage(i);

//   // Autoplay with cleanup
//   useEffect(() => {
//     if (pages <= 1 || paused) return;
//     const id = setInterval(nextPage, 4000);
//     return () => clearInterval(id);
//   }, [pages, paused]);

//   // Keyboard navigation
//   const onKeyDown = (e) => {
//     if (e.key === "ArrowRight") nextPage();
//     if (e.key === "ArrowLeft") prevPage();
//   };

//   // Swipe gestures
//   const onPointerDown = (e) => {
//     isDraggingRef.current = true;
//     startXRef.current = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
//   };
//   const onPointerUp = (e) => {
//     if (!isDraggingRef.current) return;
//     const endX = e.clientX ?? e.changedTouches?.[0]?.clientX ?? 0;
//     const delta = endX - (startXRef.current || 0);
//     isDraggingRef.current = false;
//     startXRef.current = null;
//     const threshold = 50;
//     if (delta < -threshold) nextPage();
//     if (delta > threshold) prevPage();
//   };

//     if (!total) return null;

//     const slideWidth = `${100 / spv}%`;

//     const preview = (txt) => (txt?.length > 140 ? `${txt.slice(0, 140)}…` : txt || "");
//     // Add near the top of ReviewsSlider (after hooks)
//     const getPrimaryImage = (r) => {
//             if (!r) return null;
//             return Array.isArray(r.image) ? r.image.find(Boolean) : r.image || null;
//     };

//     const getFallbackImage = (idx) => {
//         // 1) Try current review image
//         let src = getPrimaryImage(reviews[idx]);
//         if (src) return src;

//         // 2) Fallback to previous review image (walk backward)
//         for (let j = idx - 1; j >= 0; j--) {
//             const prev = getPrimaryImage(reviews[j]);
//             if (prev) return prev;
//         }

//         // 3) Final fallback: return null (keeps “No image” block)
//         return null;
//     };

//     if (isCompact) {
//         return (
//             <section
//             className="relative"
//             aria-label="Customer reviews"
//             role="region"
//             tabIndex={0}
//             >
//             <div role="list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
//                 {reviews.map((review, idx) => {
//                 const imgSrc = getPrimaryImage(review);
//                 const textOnly = !imgSrc;

//                 return (
//                     <article key={review.id} role="listitem" className="px-1">
//                     <div
//                         className={[
//                         "rounded-lg overflow-hidden shadow-sm border transition-colors",
//                         textOnly
//                             ? "bg-blue-50 border-blue-200 border-l-4 border-l-blue-500"
//                             : "bg-white border-gray-200",
//                         ].join(" ")}
//                     >
//                         {imgSrc && (
//                         <div className="relative bg-gray-100">
//                             <div className="aspect-w-2 aspect-h-1">
//                             <img
//                                 src={imgSrc}
//                                 alt={`Review ${idx + 1} image`}
//                                 className="object-cover w-full h-full"
//                                 loading="lazy"
//                             />
//                             </div>
//                         </div>
//                         )}

//                         <div className={`p-3 sm:p-4 ${textOnly ? "pt-4" : ""}`}>
//                         <h4 className="font-medium text-sm sm:text-base truncate">{review.user}</h4>
//                         <div className="flex items-center mt-1">
//                             {renderStars(review.rating)}
//                             <span className="text-xs text-gray-500 ml-2">
//                             ({review.rating?.toFixed?.(1) || review.rating})
//                             </span>
//                         </div>
//                         <p className="text-xs text-gray-500 mt-1">{formatDate(review.date)}</p>
//                         <p className={["text-sm sm:text-base mt-2", textOnly ? "text-gray-800 italic" : "text-gray-700"].join(" ")}>
//                             {review.comment}
//                         </p>
//                         </div>
//                     </div>
//                     </article>
//                 );
//                 })}
//             </div>
//             </section>
//         );
//     }

//   return (
//     <section
//       className="relative"
//       role="region"
//       aria-roledescription="carousel"
//       aria-label="Customer reviews slider"
//       tabIndex={0}
//       aria-live="off"
//       onKeyDown={onKeyDown}
//       onMouseEnter={() => setPaused(true)}
//       onMouseLeave={() => setPaused(false)}
//       onPointerDown={onPointerDown}
//       onPointerUp={onPointerUp}
//       onTouchStart={onPointerDown}
//       onTouchEnd={onPointerUp}
//     >
//       {/* Track */}
//       <div className="overflow-hidden rounded-md">
//         <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${page * 100}%)` }} >
//         {reviews.map((review, idx) => {
//             const imgSrc = getPrimaryImage(review);
//             const textOnly = !imgSrc;

//             return (
//                 <article
//                     key={review.id}
//                     role="group"
//                     aria-roledescription="slide"
//                     aria-label={`Slide ${idx + 1} of ${total}`}
//                     className="px-1 shrink-0"            // prevent shrinking
//                     style={{ flex: `0 0 ${slideWidth}`, maxWidth: slideWidth }}
//                 >
//                     <div
//                         className={[
//                         "rounded-lg overflow-hidden shadow-sm border transition-colors",
//                         textOnly
//                             ? "bg-blue-50 border-blue-200 border-l-4 border-l-blue-500"
//                             : "bg-white border-gray-200",
//                         ].join(" ")}
//                     >
//                         {/* Image wrapper: only render when an image exists */}
//                         {imgSrc && (
//                         <div className="relative bg-gray-100">
//                             <div className="aspect-w-2 aspect-h-1">
//                             <img
//                                 src={imgSrc}
//                                 alt={`Review ${idx + 1} image`}
//                                 className="object-cover w-full h-full"
//                                 loading="lazy"
//                             />
//                             </div>
//                         </div>
//                         )}

//                         {/* Text content */}
//                         <div className={`p-3 sm:p-4 ${textOnly ? "pt-4" : ""}`}>
//                             <h4 className="font-medium text-sm sm:text-base truncate">
//                                 {review.user}
//                             </h4>

//                             <div className="flex items-center mt-1">
//                                 {renderStars(review.rating)}
//                                 <span className="text-xs text-gray-500 ml-2">
//                                 ({review.rating?.toFixed?.(1) || review.rating})
//                                 </span>
//                             </div>

//                             <p className="text-xs text-gray-500 mt-1">{formatDate(review.date)}</p>

//                             <p
//                                 className={[
//                                 "text-sm sm:text-base mt-2",
//                                 textOnly ? "text-gray-800 italic" : "text-gray-700",
//                                 ].join(" ")}
//                             >
//                                 {review.comment}
//                             </p>
//                         </div>
//                     </div>
//                 </article>
//             );
//         })}
//         </div>
//       </div>

//       {/* Controls */}
//       {pages > 1 && (
//         <div className="mt-3 flex items-center justify-between">
//           {/* <button
//             onClick={prevPage}
//             className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
//             aria-label="Previous reviews"
//           >
//             Prev
//           </button> */}

//           <div role="group" aria-label="Slide selectors" className="flex gap-2">
//             {Array.from({ length: pages }).map((_, i) => (
//               <button
//                 key={i}
//                 onClick={() => goToPage(i)}
//                 aria-label={`Go to slide ${i + 1}`}
//                 aria-selected={i === page}
//                 className={`w-2.5 h-2.5 rounded-full ${i === page ? "bg-blue-600" : "bg-gray-300 hover:bg-gray-400"}`}
//               />
//             ))}
//           </div>

//           {/* <button
//             onClick={nextPage}
//             className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
//             aria-label="Next reviews"
//           >
//             Next
//           </button> */}
//         </div>
//       )}
//     </section>
//   );
// };


// // Add custom animations for product page
// const customStyles = `

//     @keyframes fadeIn {
//     0% { opacity: 0; }
//     100% { opacity: 1; }
//   }
  
//   /* ... existing animations ... */
  
//   /* Custom scrollbar for review text */
//   .custom-scrollbar::-webkit-scrollbar {
//     width: 6px;
//   }
  
//   .custom-scrollbar::-webkit-scrollbar-track {
//     background: #f1f1f1;
//     border-radius: 10px;
//   }
  
//   .custom-scrollbar::-webkit-scrollbar-thumb {
//     background: #DA9687;
//     border-radius: 10px;
//   }
  
//   .custom-scrollbar::-webkit-scrollbar-thumb:hover {
//     background: #c8847a;
//   }
  
//   /* Firefox scrollbar */
//   .custom-scrollbar {
//     scrollbar-width: thin;
//     scrollbar-color: #DA9687 #f1f1f1;
//   }

//   @keyframes fadeIn {
//     0% { opacity: 0; }
//     100% { opacity: 1; }
//   }
  
//   @keyframes pulse-border {
//     0% { box-shadow: 0 0 0 0 rgba(6, 182, 212, 0.7); }
//     70% { box-shadow: 0 0 0 10px rgba(6, 182, 212, 0); }
//     100% { box-shadow: 0 0 0 0 rgba(6, 182, 212, 0); }
//   }
  
//   @keyframes highlight-glow {
//     0%, 100% { box-shadow: 0 0 5px rgba(16, 185, 129, 0.2); }
//     50% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.6); }
//   }
  
//   @keyframes slideIn {
//     0% { transform: translateX(100%); opacity: 0; }
//     100% { transform: translateX(0); opacity: 1; }
//   }
  
//   @keyframes spin {
//     0% { transform: rotate(0deg); }
//     100% { transform: rotate(360deg); }
//   }
  
//   .animate-fadeIn {
//     animation: fadeIn 0.6s ease-out;
//   }
  
//   .animate-pulse-border {
//     animation: pulse-border 2s infinite;
//   }
  
//   .animate-highlight-glow {
//     animation: highlight-glow 1.5s infinite;
//   }
  
//   .animate-slideIn {
//     animation: slideIn 0.5s ease-out;
//   }
  
//   .animate-spin {
//     animation: spin 1s linear infinite;
//   }
  
//   /* Smooth carousel transitions */
//   .carousel-container {
//     scroll-behavior: smooth;
//   }
  
//   .product-card-hover {
//     transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
//   }
  
//   .product-card-hover:hover {
//     transform: translateY(-4px);
//     box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
//   }
// `;
// // Helper to find by slug if the page is visited directly (fallback)
// const findBySlug = (s) => translations?.products?.product?.find((p) => p?.slug === s);

// const ProductPage = ({ product: productProp }) => {
//     const { slug } = useParams();
//     const navigate = useNavigate();
//     const { addToCart } = useCart();
//     const product = useMemo(() => {
//         if (productProp) return productProp;
//         if (slug) return findBySlug(slug);
//         return null;
//     }, [productProp, slug]);
//     const [showNotification, setShowNotification] = useState(false);
//     const [showPaymentBanner, setShowPaymentBanner] = useState(true);
//     const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
//     const [checkoutError, setCheckoutError] = useState(null);
//     const [activeTab, setActiveTab] = useState('reviews');
//     const [activeImage, setActiveImage] = useState('');
//     const [quantity, setQuantity] = useState(1);
//     const [zoom, setZoom] = useState(false);
//     const [selectedColor, setSelectedColor] = useState(null);
//     const [selectedSize, setSelectedSize] = useState(null);
//     const [recentlyViewed, setRecentlyViewed] = useState([]);
//     const [currentSlide, setCurrentSlide] = useState(0);
    
    
    
//     // Get different countdown times based on product ID
//     const getCountdownTimeForProduct = (productId) => {
//         const timePatterns = {
//             1: { hours: 2, minutes: 37, seconds: 42 },
//             2: { hours: 6, minutes: 14, seconds: 29 },
//             3: { hours: 3, minutes: 58, seconds: 15 },
//             4: { hours: 1, minutes: 12, seconds: 33 },
//             5: { hours: 5, minutes: 42, seconds: 21 },
//             6: { hours: 7, minutes: 18, seconds: 46 },
//             7: { hours: 2, minutes: 23, seconds: 11 },
//             8: { hours: 4, minutes: 50, seconds: 37 },
//             9: { hours: 8, minutes: 5, seconds: 52 },
//             10: { hours: 1, minutes: 49, seconds: 18 },
//             11: { hours: 6, minutes: 32, seconds: 45 },
//             12: { hours: 3, minutes: 15, seconds: 29 },
//             default: { hours: 4, minutes: 59, seconds: 59 }
//         };
        
//         return timePatterns[productId] || timePatterns.default;
//     };
    
//     const [timeRemaining, setTimeRemaining] = useState(
//         getCountdownTimeForProduct(parseInt(id))
//     );

//     // Compute site URL (can be overridden via env at build time)
//     const SITE_URL =  'https://sacredrelm.com';

//     // SEO-friendly data
//     const productSchema = {
//         "@context": "https://schema.org/",
//         "@type": "Product",
//         "name": product?.name || "",
//         "image": product?.photo?.image1 || "",
//         "sku": `SKU-${product?.id || "0000"}`,
//         "brand": {
//             "@type": "Sacredrelm",
//             "name": "Sacredrelm"
//         },
//         "offers": {
//             "@type": "Offer",
//             "url": product ? `${SITE_URL}${createProductUrl(product.id, product.name)}` : window.location.href,
//             "priceCurrency": "INR",
//             "price": product?.cost || 0,
//             "availability": "https://schema.org/InStock",
//             "itemCondition": "https://schema.org/NewCondition"
//         }
//     };

//     if (!product) return <div>Product not found</div>;

//     // Check if the product has free accessories directly from the product data
//     const hasFreeAccessories = product?.freeAccessories && product.freeAccessories.length > 0;
//     // Use the accessories from product data if available
//     const freeAccessories = product?.freeAccessories || [];

//     useEffect(() => {
//         // Set the first image as active when product loads
//         if (product && product.photo && product.photo.image1) {
//             setActiveImage(product.photo.image1);
//         }
        
//         // Reset timer when product changes
//         setTimeRemaining(getCountdownTimeForProduct(parseInt(id)));
        
//         // Scroll to top when product changes
//         window.scrollTo(0, 0);
        
//         // Handle recently viewed products storage
//         if (product) {
//             const allProducts = translations.products.product;
//             const otherProducts = allProducts.filter(p => p.id !== product.id);
//             setRecentlyViewed(otherProducts);
//         }

//         // Hide payment banner after scrolling
//         const handleScroll = () => {
//             if (window.scrollY > 300) {
//                 setShowPaymentBanner(false);
//             } else {
//                 setShowPaymentBanner(true);
//             }
//         };
        
//         window.addEventListener('scroll', handleScroll);
//         return () => window.removeEventListener('scroll', handleScroll);
//     }, [product, id]);

//     useEffect(() => {
//         // Countdown timer for urgency
//         const countdownInterval = setInterval(() => {
//             setTimeRemaining(prev => {
//                 if (prev.seconds > 0) {
//                     return { ...prev, seconds: prev.seconds - 1 };
//                 } else if (prev.minutes > 0) {
//                     return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
//                 } else if (prev.hours > 0) {
//                     return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
//                 } else {
//                     // Reset timer when it reaches 0
//                     return { hours: 4, minutes: 59, seconds: 59 };
//                 }
//             });
//         }, 1000);
//         return () => {
//             clearInterval(countdownInterval);
//         };
//     }, [product]);

//     const handleAddToCart = () => {
//         const cartItem = {
//             ...product,
//             quantity: quantity,
//             cartItemId: Date.now() + Math.random(),
//             addedAt: new Date().toISOString()
//         };
//         addToCart(cartItem);
//         setShowNotification(true);

//         // Update localStorage
//         const cartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
//         cartItems.push(cartItem);
//         localStorage.setItem('cartItems', JSON.stringify(cartItems));

//         setTimeout(() => {
//             setShowNotification(false);
//         }, 2000);
//     };

//     const handleBuyNow = async () => {
//         setIsCheckoutLoading(true);
//         setCheckoutError(null);

//         try {
//             // Get existing cart items
//             const existingCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];

//             // Create current product item
//             const currentProductItem = {
//                 name: product.name,
//                 quantity: quantity,
//                 price: Number(product.cost),
//                 cost: Number(product.cost),
//             };

//             // Process existing items
//             const processedCartItems = existingCartItems.map(item => ({
//                 name: item.name,
//                 quantity: 1,
//                 price: Number(item.cost),
//                 cost: Number(item.cost),
//             }));

//             // Combine all items
//             const allItems = [...processedCartItems, currentProductItem];

//             // Calculate total
//             const totalAmount = allItems.reduce((total, item) => {
//                 return total + (Number(item.price) || Number(item.cost) || 0) * (item.quantity || 1);
//             }, 0);

//             // Navigate to billing page
//             navigate('/billing', {
//                 state: {
//                     items: allItems,
//                     totalAmount: totalAmount,
//                     quantity: allItems.length,
//                     paymentMode: 'online',
//                 },
//             });
//         } catch (error) {
//             setCheckoutError(error.message);
//             console.error('Checkout failed:', error);
//         } finally {
//             setIsCheckoutLoading(false);
//         }
//     };

//     const renderStars = (rating) => {
//         const stars = [];
//         const fullStars = Math.floor(rating);
//         const hasHalfStar = rating - fullStars >= 0.5;
        
//         for (let i = 1; i <= 5; i++) {
//             if (i <= fullStars) {
//                 stars.push(<FaStar key={i} className="text-yellow-400" />);
//             } else if (i === fullStars + 1 && hasHalfStar) {
//                 stars.push(<FaStarHalfAlt key={i} className="text-yellow-400" />);
//             } else {
//                 stars.push(<FaRegStar key={i} className="text-yellow-400" />);
//             }
//         }
        
//         return <div className="flex">{stars}</div>;
//     };
//     const formatDate = (dateString) => {
//         const options = { year: 'numeric', month: 'long', day: 'numeric' };
//         return new Date(dateString).toLocaleDateString('en-US', options);
//     };
//     // Use product-specific reviews from data
//     const productReviews = product?.reviews || [];
        
//     // Calculate average rating from product reviews
//     const calculateAverageRating = () => {
//         if (!productReviews.length) return 4.5; // Default if no reviews
    
//         const total = productReviews.reduce((sum, review) => sum + review.rating, 0);
//         return (total / productReviews.length).toFixed(1);
//     };

//     // Calculate rating distribution percentages
//     const calculateRatingDistribution = () => {
//         if (!productReviews.length) {
//             // Default distribution if no reviews
//             return {
//                 5: 70,
//                 4: 20,
//                 3: 5,
//                 2: 3,
//                 1: 2
//             };
//         }
            
//         const distribution = {5: 0, 4: 0, 3: 0, 2: 0, 1: 0};
    
//         productReviews.forEach(review => {
//             const rating = Math.floor(review.rating);
//             distribution[rating] = (distribution[rating] || 0) + 1;
//         });
            
//         // Convert to percentages
//         const total = productReviews.length;
//         Object.keys(distribution).forEach(rating => {
//             distribution[rating] = Math.round((distribution[rating] / total) * 100);
//         });
            
//         return distribution;
//     };
        
//     const ratingDistribution = calculateRatingDistribution();
//     const averageRating = calculateAverageRating();

//     // Enhance the countdown timer to show different messages based on time remaining
//     const getUrgencyMessage = () => {
//         const totalSeconds = timeRemaining.hours * 3600 + timeRemaining.minutes * 60 + timeRemaining.seconds;
        
//         if (hasFreeAccessories) {
//             if (totalSeconds < 3600) {
//                 return 'HURRY! Free accessories offer ends soon!';
//             } else {
//                 return 'Order now to receive free accessories!';
//             }
//         } else {
//             if (totalSeconds < 3600) {
//                 return 'FINAL chance! Price increases soon!';
//             } else {
//                 return 'Limited time offer! Hurry before price goes up!';
//             }
//         }
//     };

//     // Carousel functions for recently viewed products
//     const productsPerSlide = 4;
//     const totalSlides = Math.ceil(recentlyViewed.length / productsPerSlide);
    
//     const nextSlide = useCallback(() => {
//         setCurrentSlide((prev) => (prev + 1) % totalSlides);
//     }, [totalSlides]);
    
//     // Auto-slide functionality
//     useEffect(() => {
//         if (recentlyViewed.length > productsPerSlide) {
//             const slideInterval = setInterval(() => {
//                 nextSlide();
//             }, 4000);

//             return () => clearInterval(slideInterval);
//         }
//     }, [recentlyViewed.length, nextSlide]);

//     // Don't render if product not found
//     if (!product) {
//         return <div>Product not found</div>;
//     }
    
//     const prevSlide = () => {
//         setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
//     };
    
//     const goToSlide = (slideIndex) => {
//         setCurrentSlide(slideIndex);
//     };

//     const highlightBraces = (text) => {
//         if (!text) return null;
//         const parts = [];
//         const regex = /{([^}]+)}/g;
//         let last = 0, m;
//         while ((m = regex.exec(text)) !== null) {
//             if (m.index > last) parts.push(text.slice(last, m.index));
//             parts.push(<strong key={parts.length}>{m[1]}</strong>);
//             last = regex.lastIndex;
//         }
//         if (last < text.length) parts.push(text.slice(last));
//         return parts;
//     };

//     const renderFullDescription = (fullDescription) => {
//         if (!fullDescription) return null;
//         const lines = fullDescription.split('\n');

//         const out = [];
//         let listType = null; // 'ul' | 'ol'
//         let items = [];

//         const flushList = () => {
//             if (!listType || items.length === 0) return;
//             if (listType === 'ul') {
//             out.push(
//                 <ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1">
//                 {items.map((it, i) => <li key={i}>{it}</li>)}
//                 </ul>
//             );
//             } else {
//             out.push(
//                 <ol key={`ol-${out.length}`} className="list-decimal pl-5 space-y-1">
//                 {items.map((it, i) => <li key={i}>{it}</li>)}
//                 </ol>
//             );
//             }
//             listType = null;
//             items = [];
//         };

//         for (let i = 0; i < lines.length; i++) {
//             const raw = (lines[i] || '').trim();
//             if (!raw) { flushList(); continue; }

//             // Heading lines starting with "##" or "## "
//             if (raw.startsWith('##')) {
//             flushList();
//             const title = raw.replace(/^##\s*/, '');
//             out.push(
//                 <h3 key={`h-${out.length}`} className="text-lg sm:text-xl font-semibold text-gray-800 mt-3">
//                 {highlightBraces(title)}
//                 </h3>
//             );
//             continue;
//             }

//             // Ordered list: "1. Something"
//             const olMatch = raw.match(/^\d+\.\s+(.*)$/);
//             if (olMatch) {
//             if (listType !== 'ol') { flushList(); listType = 'ol'; }
//             items.push(<span>{highlightBraces(olMatch[1])}</span>);
//             continue;
//             }

//             // Unordered list: "- Something"
//             const ulMatch = raw.match(/^-\s+(.*)$/);
//             if (ulMatch) {
//             if (listType !== 'ul') { flushList(); listType = 'ul'; }
//             items.push(<span>{highlightBraces(ulMatch[1])}</span>);
//             continue;
//             }

//             // Plain paragraph
//             flushList();
//             out.push(
//             <p key={`p-${out.length}`} className="break-words overflow-wrap-anywhere">
//                 {highlightBraces(raw)}
//             </p>
//             );
//         }
//         flushList();
//         return out;
//     };

//     return (
//         <div>
//             <style>{customStyles}</style>
//             {/* SEO Optimization */}
//             <Helmet>
//                 <title>{product.metaTitle ? `${product.metaTitle} | Sacred Relm` : `${product.name} | Sacred Relm`}</title>
//                 <meta name="description" content={product.metaDescription || `Buy ${product.name} - ${product.description?.substring(0, 150) || 'Premium spiritual product at the best price'}`} />
//                 <meta name="keywords" content={
//                     product.focusKeyword && product.tags 
//                         ? `${product.focusKeyword}, ${product.tags.join(', ')}, ${product.name}, ${product.category}, spiritual items, sacred, buy online`
//                         : `${product.name}, ${product.category}, spiritual items, sacred, buy online`
//                 } />
//                 <link rel="canonical" href={`${SITE_URL}${createProductUrl(product.id, product.name)}`} />
//                 <meta property="og:title" content={product.metaTitle ? `${product.metaTitle} | Sacred Relm` : `${product.name} | Sacred Relm`} />
//                 <meta property="og:description" content={product.metaDescription || product.description?.substring(0, 150) || 'Premium spiritual product at the best price'} />
//                 <meta property="og:image" content={`${SITE_URL}${product.photo.image1}`} />
//                 <meta property="og:url" content={`${SITE_URL}${createProductUrl(product.id, product.name)}`} />
//                 <meta property="og:type" content="product" />
//                 <meta name="twitter:card" content="summary_large_image" />
//                 <script type="application/ld+json">
//                     {JSON.stringify(productSchema)}
//                 </script>
//             </Helmet>
//              <BusinessSchema pageType="product" />
//             <div>
//                 <PageHeader title={product.name} subtitle="Home > Store > Product" />
                
//                 {/* Notification */}
//                 {showNotification && (
//                     <div className="fixed top-16 sm:top-24 left-0 sm:left-1/2 transform sm:-translate-x-1/2 z-50 w-full sm:w-[90%] sm:max-w-md px-4 sm:px-0">
//                         <div className="bg-green-500 text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg flex items-center justify-center">
//                             <FaCheck className="mr-2" />
//                             <span>Added to cart successfully!</span>
//                         </div>
//                     </div>
//                 )}

//                 {/* Checkout Error Notification */}
//                 {checkoutError && (
//                     <div className="fixed top-16 sm:top-24 left-0 sm:left-1/2 transform sm:-translate-x-1/2 z-50 w-full sm:w-[90%] sm:max-w-md px-4 sm:px-0">
//                         <div className="bg-red-500 text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg flex items-center justify-between">
//                             <div className="flex items-center">
//                                 <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                                 </svg>
//                                 <span className="text-sm">{checkoutError}</span>
//                             </div>
//                             <button
//                                 onClick={() => setCheckoutError(null)}
//                                 className="ml-2 text-white hover:text-gray-200"
//                             >
//                                 ×
//                             </button>
//                         </div>
//                     </div>
//                 )}

//                 <div className="bg-[#faebd7] mx-auto px-3 sm:px-4 py-3 sm:py-8">
//                     <div className="">
//                         {/* Product Top Section */}
//                         <div className="bg-[#faebd7] rounded-xl sm:rounded-2xl shadow-sm overflow-hidden">
//                             <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
//                                 {/* Product Images - Left Side */}
//                                 <div className="p-3 sm:p-6 bg-[#faebd7] border-b md:border-r md:border-b-0 border-gray-100 relative">
//                                     {/* Main Image */}
//                                     <div 
//                                         className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg ${zoom ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
//                                         onClick={() => setZoom(!zoom)}
//                                     >
//                                         <img
//                                             src={activeImage}
//                                             alt={product.name}
//                                             className={`w-full h-[250px] sm:h-[350px] md:h-[500px] object-contain transition-all duration-300 ${zoom ? 'scale-150' : 'scale-100'}`}
//                                             loading="eager"
//                                             title={product.name}
//                                         />
//                                     </div>
                                    
//                                     {/* Thumbnail Gallery */}
//                                     <div className="grid grid-cols-4 sm:grid-cols-5 gap-1 sm:gap-4 mt-3 sm:mt-6">
//                                         {Object.values(product.photo).map((image, index) => (
//                                             <div
//                                                 key={index}
//                                                 className={`border-2 rounded-lg overflow-hidden cursor-pointer transition-all hover:shadow-md ${activeImage === image ? 'border-blue-500 shadow-sm' : 'border-transparent'}`}
//                                                 onClick={() => setActiveImage(image)}
//                                             >
//                                                 <img
//                                                     src={image}
//                                                     alt={`${product.name} view ${index + 1}`}
//                                                     className="w-full h-full object-contain"
//                                                     loading="lazy"
//                                                 />
//                                             </div>
//                                         ))}
//                                     </div>
//                                 </div>

//                                 {/* Product Info - Right Side */}
//                                 <div className="p-3 sm:p-8 space-y-3 sm:space-y-6">
//                                     {/* Product Title & Rating */}
//                                     <div className="space-y-2">
//                                         <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{product.name}</h1>
//                                         <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
//                                             {product.description || 'A premium quality product crafted with attention to detail and designed for everyday use.'}
//                                         </p>
//                                         <div className="flex flex-wrap items-center gap-2 sm:gap-4">
//                                             <div className="flex items-center gap-2">
//                                                 {renderStars(4.5)}
//                                             </div>
//                                         </div>
//                                     </div>

//                                     {/* Price Section */}
//                                     <div className="bg-[#faebd7] p-3 sm:p-4 rounded-lg">
//                                         <div className="flex items-center space-x-2 sm:space-x-4">
//                                             <p className="text-2xl sm:text-3xl font-bold text-blue-600">₹{product.cost.toLocaleString()}</p>
//                                             <p className="text-sm sm:text-lg text-gray-500 line-through">₹{Math.floor(product.cost * 1.2).toLocaleString()}</p>
//                                             <span className="bg-red-100 text-red-800 px-2 py-1 rounded-md text-xs sm:text-sm font-medium">20% OFF</span>
//                                         </div>
//                                         <p className="text-xs text-gray-500 mt-2">* Inclusive of all taxes</p>
//                                     </div>

//                                     {/* Free Accessories section - keeping your existing code */}
//                                     {hasFreeAccessories && (
//                                         <div className="bg-gradient-to-r from-orange-50 to-blue-50 border-2 border-dashed border-orange-200 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3 relative overflow-hidden transform transition-all hover:scale-[1.01] animate-fadeIn">
//                                             <div className="absolute -right-12 -top-4 bg-red-500 text-white px-12 py-1 transform rotate-45 shadow-md">
//                                                 <span className="text-xs font-bold">LIMITED TIME</span>
//                                             </div>
                                            
//                                             <h3 className="flex items-center text-orange-700 font-bold text-lg sm:text-xl">
//                                                 <FaGift className="mr-2 text-orange-600" /> 
//                                                 Free Premium Accessories
//                                             </h3>
                                            
//                                             <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-400 rounded-lg p-3 sm:p-4 relative overflow-hidden shadow-sm">
//                                                 <div className="absolute top-0 right-0 h-full w-1/3 bg-gradient-to-l from-yellow-100 to-transparent opacity-70"></div>
                                                
//                                                 <div className="flex items-start space-x-3">
//                                                     <div className="p-2 bg-amber-100 rounded-full">
//                                                         <FaInfoCircle className="text-amber-500 text-lg animate-pulse" />
//                                                     </div>
//                                                     <div>
//                                                         <p className="font-medium text-amber-800 text-sm sm:text-base mb-1">
//                                                             ONLINE PAYMENT EXCLUSIVE
//                                                         </p>
//                                                         <p className="text-amber-700 text-sm">
//                                                             These premium accessories (worth <span className="font-bold">₹{Math.floor(product.cost * 0.2)}</span>) are <span className="underline font-bold">only included with online payment methods</span>. Not available with cash on delivery.
//                                                         </p>
//                                                     </div>
//                                                 </div>
//                                             </div>
                                            
//                                             <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 mt-3">
//                                                 {freeAccessories.map((item, idx) => (
//                                                     <div key={idx} className="flex items-center bg-[#faebd7] p-2 sm:p-3 rounded-lg shadow-md border border-orange-100 hover:border-orange-300 transition-all">
//                                                         <div className="relative">
//                                                             <img 
//                                                                 src={item.image} 
//                                                                 alt={item.name} 
//                                                                 className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-md"
//                                                                 loading="lazy"
//                                                             />
//                                                             <div className="absolute -bottom-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
//                                                                 <FaCheck size={10} />
//                                                             </div>
//                                                         </div>
//                                                         <div className="ml-3">
//                                                             <h4 className="text-sm sm:text-base font-medium text-gray-800">{item.name}</h4>
//                                                             <p className="text-xs text-gray-600 hidden sm:block">{item.description}</p>
//                                                         </div>
//                                                     </div>
//                                                 ))}
//                                             </div>
                                            
//                                             <div className="flex justify-center mt-1">
//                                                 <span className="text-sm text-gray-500 italic">
//                                                     Tap "Buy Now" to secure these free accessories today!
//                                                 </span>
//                                             </div>
//                                         </div>
//                                     )}

//                                     {/* Short description */}
//                                     {/* <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
//                                         {product.description || 'A premium quality product crafted with attention to detail and designed for everyday use.'}
//                                     </p> */}

//                                     {/* Quantity Selector */}
//                                     <div className="flex items-center space-x-4">
//                                         <span className="text-gray-700 text-sm sm:text-base">Quantity:</span>
//                                         <div className="flex items-center border border-gray-300 rounded-md">
//                                             <button 
//                                                 onClick={() => quantity > 1 && setQuantity(quantity - 1)}
//                                                 className="px-3 py-2 text-lg border-r hover:bg-gray-100 transition"
//                                                 aria-label="Decrease quantity"
//                                             >
//                                                 -
//                                             </button>
//                                             <span className="px-4 py-2 min-w-[40px] text-center">{quantity}</span>
//                                             <button 
//                                                 onClick={() => setQuantity(quantity + 1)}
//                                                 className="px-3 py-2 text-lg border-l hover:bg-gray-100 transition"
//                                                 aria-label="Increase quantity"
//                                             >
//                                                 +
//                                             </button>
//                                         </div>
//                                     </div>

//                                     {/* Enhanced Action Buttons with Shopify Integration */}
//                                     <div className="space-y-3 pt-2">
//                                         {/* Countdown Timer */}
//                                         <div className="bg-gradient-to-r from-red-50 via-red-100 to-red-50 border border-red-200 rounded-lg p-2 sm:p-3 text-center animate-pulse-border">
//                                             <p className="text-red-700 font-medium text-xs sm:text-sm">Special offer ends in:</p>
//                                             <div className="flex justify-center items-center space-x-1 sm:space-x-2 mt-1">
//                                                 <div className="bg-red-600 text-white px-1 sm:px-2 py-1 rounded text-xs sm:text-base">
//                                                     <span className="font-bold">{String(timeRemaining.hours).padStart(2, '0')}</span>
//                                                 </div>
//                                                 <span className="font-bold text-red-600">:</span>
//                                                 <div className="bg-red-600 text-white px-1 sm:px-2 py-1 rounded text-xs sm:text-base">
//                                                     <span className="font-bold">{String(timeRemaining.minutes).padStart(2, '0')}</span>
//                                                 </div>
//                                                 <span className="font-bold text-red-600">:</span>
//                                                 <div className="bg-red-600 text-white px-1 sm:px-2 py-1 rounded text-xs sm:text-base">
//                                                     <span className="font-bold">{String(timeRemaining.seconds).padStart(2, '0')}</span>
//                                                 </div>
//                                             </div>
//                                             <p className="text-xxs sm:text-xs text-red-600 mt-1">
//                                                 {getUrgencyMessage()}
//                                             </p>
//                                         </div>

//                                         {/* Action Buttons */}
//                                         <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
//                                             <button
//                                                 onClick={handleAddToCart}
//                                                 className="bg-[#DA9687] text-white px-4 sm:px-8 py-3 rounded-lg hover:bg-opacity-90 hover:shadow-md transition-all flex-1 flex justify-center items-center text-sm sm:text-base"
//                                                 disabled={isCheckoutLoading}
//                                             >
//                                                 <FaShoppingCart className="mr-1 sm:mr-2" />
//                                                 Add to Cart
//                                             </button>
                                            
//                                             {/* Enhanced Buy Now Button with Shopify Integration */}
//                                             <button
//                                                 onClick={handleBuyNow}
//                                                 disabled={isCheckoutLoading}
//                                                 className={`relative overflow-hidden ${hasFreeAccessories 
//                                                     ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg' 
//                                                     : 'bg-blue-600 hover:bg-blue-700'} 
//                                                     text-white px-4 sm:px-8 py-3 rounded-lg transition-all flex-1 flex justify-center items-center group text-sm sm:text-base font-bold
//                                                     ${isCheckoutLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
//                                             >
//                                                 {/* Loading Spinner */}
//                                                 {isCheckoutLoading && (
//                                                     <div className="absolute inset-0 bg-blue-700 bg-opacity-50 flex items-center justify-center">
//                                                         <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
//                                                     </div>
//                                                 )}

//                                                 {/* Pulsing border effect */}
//                                                 <span className="absolute inset-0 border-2 border-white rounded-lg opacity-0 group-hover:opacity-30 animate-pulse"></span>
                                                
//                                                 {/* Shimmer effect */}
//                                                 {hasFreeAccessories && (
//                                                     <span className="absolute inset-0 w-full h-full">
//                                                         <span className="absolute top-0 left-0 w-full h-full bg-white opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
//                                                     </span>
//                                                 )}
                                                
//                                                 {/* Icon and text */}
//                                                 <div className="flex items-center">
//                                                     <FaShoppingCart className="mr-1 sm:mr-2" />
//                                                     <span className="font-bold tracking-wider">
//                                                         {isCheckoutLoading ? 'PROCESSING...' : 'BUY NOW'}
//                                                     </span>
//                                                     {hasFreeAccessories && <FaGift className="ml-1 sm:ml-2 animate-pulse" />}
//                                                 </div>
                                                
//                                                 {/* "Act now" label */}
//                                                 {!isCheckoutLoading && (
//                                                     <div className="absolute -bottom-1 -right-1 transform translate-x-1/4 translate-y-1/4 bg-yellow-400 text-blue-800 text-[8px] sm:text-[9px] uppercase font-extrabold px-1 sm:px-2 py-0.5 rounded-full tracking-wide rotate-12 shadow-sm">
//                                                         Act now!
//                                                     </div>
//                                                 )}
//                                             </button>
//                                         </div>
//                                         <div className='flex gap-5'>
//                                             <span className="text-green-600 flex items-center gap-1">
//                                                 <FaCheck size={14} />
//                                                 In Stock
//                                             </span>
//                                             <span className="hidden sm:inline text-gray-500">|</span>
//                                             <span className="text-blue-600 flex items-center gap-1">
//                                                 <FaTruck size={14} />
//                                                 Fast Shipping
//                                             </span>
//                                         </div>

//                                         {/* Amazon Button */}
//                                         {product.amazon && (
//                                             <div id="amazon" className="pt-2">
//                                                 <a 
//                                                     id="amazon" 
//                                                     href={product.amazon} 
//                                                     target="_blank" 
//                                                     rel="noopener noreferrer" 
//                                                     className="amazon button w-full text-black px-4 sm:px-8 rounded-lg transition-all flex justify-center items-start text-sm sm:text-base font-bold shadow-lg transform hover:scale-105"
//                                                 >
//                                                     <span id="amazon" className="tracking-wider font-bold pt-[4px]">ALSO AVAILABLE ON </span>
//                                                     <img id="amazon" src={amazon} className="w-20" alt="Amazon" />
//                                                 </a>
//                                                 <p className="text-xs text-gray-500 text-center mt-1">
//                                                     Also available on Amazon with fast delivery
//                                                 </p>
//                                             </div>
//                                         )}
                                        
//                                     </div>
                                    
//                                 </div>
//                             </div>
//                         </div>
//                         <div className="text-gray-700 leading-relaxed text-sm sm:text-base space-y-3 w-full">
//                             <h1 className='text-3xl font-bold'>Product Description</h1>
                            
//                             {renderFullDescription(product.fullDescription)}
//                         </div>

//                         <div className='flex flex-col md:flex-row lg:flex-row'>                        
//                             {/* Benefits Section */}
//                             {product.benefits && product.benefits.length > 0 && (
//                                 <div className="bg-[#faebd7] rounded-lg shadow-sm overflow-hidden mt-3 sm:mt-4 md:mt-6 p-3 sm:p-4 md:p-6 w-full">
//                                     <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4 flex items-center">
//                                         <FaCheck className="mr-1.5 sm:mr-2 text-[#DA9687] " size={14} />
//                                         <span>Key Benefits</span>
//                                     </h2>
//                                     <div className="grid grid-cols-1 gap-2 sm:gap-2.5 md:gap-3 w-full">
//                                         {product.benefits.map((benefit, index) => (
//                                             <div key={index} className="flex items-start gap-2 sm:gap-2.5 md:gap-3 bg-white bg-opacity-50 p-2 sm:p-2.5 md:p-3 rounded-md sm:rounded-lg border border-gray-200 w-full">
//                                                 <FaCheck className="text-[#DA9687] mt-0.5 sm:mt-1" size={12} />
//                                                 <p className="text-gray-700 text-xs sm:text-sm md:text-base break-words leading-relaxed">{benefit}</p>
//                                             </div>
//                                         ))}
//                                     </div>
//                                 </div>
//                             )}

//                             {/* Placement Guidelines Section */}
//                             {(product.placement || product.howToUse) && (
//                                 <div className="bg-[#faebd7] rounded-xl sm:rounded-2xl shadow-sm overflow-hidden mt-4 sm:mt-6 p-4 sm:p-6 w-full">
//                                     <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 flex items-center">
//                                         <FaInfoCircle className="mr-2 text-[#DA9687] " />
//                                         {product.placement ? 'Placement Guidelines' : 'How to Use'}
//                                     </h2>
//                                     <div className="space-y-3 w-full">
//                                         {(product.placement || product.howToUse).map((item, index) => (
//                                             <div key={index} className="flex items-start gap-3 bg-white bg-opacity-50 p-3 rounded-lg border border-gray-200 w-full">
//                                                 <span className="text-[#DA9687] font-bold text-lg ">{index + 1}.</span>
//                                                 <p className="text-gray-700 text-sm sm:text-base flex-1 break-words overflow-wrap-anywhere">{item}</p>
//                                             </div>
//                                         ))}
//                                     </div>
//                                 </div>
//                             )}
//                         </div>

//                         {/* FAQ Section */}
//                         {product.faqs && product.faqs.length > 0 && (
//                             <div className="bg-[#faebd7] rounded-xl sm:rounded-2xl shadow-sm overflow-hidden mt-4 sm:mt-6 p-4 sm:p-6 w-full">
//                                 <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 flex items-center">
//                                     <FaQuestionCircle className="mr-2 text-[#DA9687] " />
//                                     Frequently Asked Questions
//                                 </h2>
//                                 <div className="space-y-4 w-full">
//                                     {product.faqs.map((faq, index) => (
//                                         <div key={index} className="bg-white bg-opacity-50 p-4 rounded-lg border border-gray-200 w-full">
//                                             <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 flex items-start">
//                                                 <span className="text-[#DA9687] mr-2 ">Q{index + 1}.</span>
//                                                 <span className="break-words overflow-wrap-anywhere flex-1">{faq.question}</span>
//                                             </h3>
//                                             <p className="text-gray-700 text-sm sm:text-base pl-8 break-words overflow-wrap-anywhere">
//                                                 {faq.answer}
//                                             </p>
//                                         </div>
//                                     ))}
//                                 </div>
//                             </div>
//                         )}

//                         {activeTab === 'reviews' && (
//                             <div>
//                                 <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Customer Reviews</h2>

//                                 {/* Rating summary + distribution */}
//                                 <div className="flex flex-col md:flex-row md:items-center gap-4 sm:gap-8 mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-gray-200">
//                                 <div className="text-center md:border-r md:border-gray-200 md:pr-8">
//                                     <div className="text-4xl sm:text-5xl font-bold text-gray-900">{averageRating}</div>
//                                     <div className="flex justify-center mt-2">
//                                     {renderStars(parseFloat(averageRating))}
//                                     </div>
//                                     <div className="text-xs sm:text-sm text-gray-500 mt-1">Based on {productReviews.length} reviews</div>
//                                 </div>

//                                 <div className="flex-1">
//                                     <div className="flex items-center mb-1">
//                                     <span className="w-9 sm:w-12 text-xs sm:text-sm">5 star</span>
//                                     <div className="flex-1 h-2 mx-2 bg-gray-200 rounded-full">
//                                         <div className="h-2 bg-yellow-400 rounded-full" style={{ width: `${ratingDistribution[5]}%` }} />
//                                     </div>
//                                     <span className="w-9 sm:w-12 text-right text-xs sm:text-sm">{ratingDistribution[5]}%</span>
//                                     </div>
//                                     <div className="flex items-center mb-1">
//                                     <span className="w-9 sm:w-12 text-xs sm:text-sm">4 star</span>
//                                     <div className="flex-1 h-2 mx-2 bg-gray-200 rounded-full">
//                                         <div className="h-2 bg-yellow-400 rounded-full" style={{ width: `${ratingDistribution[4]}%` }} />
//                                     </div>
//                                     <span className="w-9 sm:w-12 text-right text-xs sm:text-sm">{ratingDistribution[4]}%</span>
//                                     </div>
//                                     <div className="flex items-center mb-1">
//                                     <span className="w-9 sm:w-12 text-xs sm:text-sm">3 star</span>
//                                     <div className="flex-1 h-2 mx-2 bg-gray-200 rounded-full">
//                                         <div className="h-2 bg-yellow-400 rounded-full" style={{ width: `${ratingDistribution[3]}%` }} />
//                                     </div>
//                                     <span className="w-9 sm:w-12 text-right text-xs sm:text-sm">{ratingDistribution[3]}%</span>
//                                     </div>
//                                     <div className="flex items-center mb-1">
//                                     <span className="w-9 sm:w-12 text-xs sm:text-sm">2 star</span>
//                                     <div className="flex-1 h-2 mx-2 bg-gray-200 rounded-full">
//                                         <div className="h-2 bg-yellow-400 rounded-full" style={{ width: `${ratingDistribution[2]}%` }} />
//                                     </div>
//                                     <span className="w-9 sm:w-12 text-right text-xs sm:text-sm">{ratingDistribution[2]}%</span>
//                                     </div>
//                                     <div className="flex items-center mb-1">
//                                     <span className="w-9 sm:w-12 text-xs sm:text-sm">1 star</span>
//                                     <div className="flex-1 h-2 mx-2 bg-gray-200 rounded-full">
//                                         <div className="h-2 bg-yellow-400 rounded-full" style={{ width: `${ratingDistribution[1]}%` }} />
//                                     </div>
//                                     <span className="w-9 sm:w-12 text-right text-xs sm:text-sm">{ratingDistribution[1]}%</span>
//                                     </div>
//                                 </div>
//                                 </div>

//                                 {/* Replace the old mapped list with the slider below */}
//                                 {productReviews?.length > 0 && (
//                                 <ReviewsSlider
//                                     reviews={productReviews}
//                                     renderStars={renderStars}
//                                     formatDate={formatDate}
//                                 />
//                                 )}
//                             </div>
//                         )}


//                         {recentlyViewed.length > 0 && (
//                             <div className="mb-6 sm:mb-12">
//                                 <div className="flex justify-between items-center mb-3 sm:mb-6">
//                                     <h2 className="text-lg sm:text-2xl font-bold">All Products</h2>
//                                     {totalSlides > 1 && (
//                                         <div className="flex items-center gap-2">
//                                             <button 
//                                                 onClick={prevSlide}
//                                                 className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
//                                                 aria-label="Previous products"
//                                             >
//                                                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
//                                                 </svg>
//                                             </button>
//                                             <button 
//                                                 onClick={nextSlide}
//                                                 className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
//                                                 aria-label="Next products"
//                                             >
//                                                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
//                                                 </svg>
//                                             </button>
//                                         </div>
//                                     )}
//                                 </div>
                                
//                                 {/* Carousel Container */}
//                                 <div className="relative overflow-hidden">
//                                     <div 
//                                         className="flex transition-transform duration-500 ease-in-out"
//                                         style={{ transform: `translateX(-${currentSlide * 100}%)` }}
//                                     >
//                                         {Array.from({ length: totalSlides }).map((_, slideIndex) => (
//                                             <div key={slideIndex} className="min-w-full">
//                                                 <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-6">
//                                                     {recentlyViewed
//                                                         .slice(slideIndex * productsPerSlide, (slideIndex + 1) * productsPerSlide)
//                                                         .map((recentProduct) => (
//                                                         <div key={recentProduct.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden product-card-hover group animate-slideIn">
//                                                             <div className="aspect-w-2 bg-gray-200 relative overflow-hidden">
//                                                                 <img 
//                                                                     src={recentProduct.photo.image1} 
//                                                                     alt={recentProduct.name} 
//                                                                     className="w-full sm:h-48 md:h-72 lg:h-72  transform group-hover:scale-110 transition duration-500"
//                                                                     loading="lazy"
//                                                                 />
//                                                                 <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
//                                                                     <button 
//                                                                         onClick={() => navigate(`/product/${recentProduct.id}`)}
//                                                                         className="bg-white text-gray-900 px-3 py-1 sm:px-4 sm:py-2 rounded-lg transform scale-95 group-hover:scale-100 transition-transform font-medium text-xs sm:text-sm shadow-lg"
//                                                                     >
//                                                                         View Details
//                                                                     </button>
//                                                                 </div>
//                                                                 {recentProduct.isNew && (
//                                                                     <div className="absolute top-2 left-2">
//                                                                         <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
//                                                                             NEW
//                                                                         </span>
//                                                                     </div>
//                                                                 )}
//                                                             </div>
//                                                             <div className="p-2 sm:p-4">
//                                                                 <h3 className="font-medium truncate group-hover:text-blue-600 transition text-xs sm:text-base line-clamp-2">{recentProduct.name}</h3>
//                                                                 <div className="flex items-center mt-1 scale-75 sm:scale-100 origin-left">
//                                                                     {renderStars(recentProduct.rating || 4)}
//                                                                     <span className="text-xs text-gray-500 ml-1">({recentProduct.rating || 4.0})</span>
//                                                                 </div>
//                                                                 <div className="mt-2 flex items-center justify-between">
//                                                                     <div>
//                                                                         <div className="font-bold text-xs sm:text-base text-blue-600">₹{recentProduct.cost.toLocaleString()}</div>
//                                                                         <div className="text-xs text-gray-500 line-through">₹{Math.floor(recentProduct.cost * 1.2).toLocaleString()}</div>
//                                                                     </div>
//                                                                     <div className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium">
//                                                                         20% OFF
//                                                                     </div>
//                                                                 </div>
//                                                                 <button 
//                                                                     onClick={() => navigate(`/product/${recentProduct.id}`)} 
//                                                                     className="mt-2 sm:mt-3 w-full bg-gradient-to-r from-[#DA9687] to-[#c8847a] text-white py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium hover:from-[#c8847a] hover:to-[#b67469] transition-all duration-300 transform hover:scale-105 shadow-md"
//                                                                 >
//                                                                     Shop Now
//                                                                 </button>
//                                                             </div>
//                                                         </div>
//                                                     ))}
//                                                 </div>
//                                             </div>
//                                         ))}
//                                     </div>
//                                 </div>
                                
//                                 {/* Pagination Dots */}
//                                 {totalSlides > 1 && (
//                                     <div className="flex justify-center mt-4 gap-2">
//                                         {Array.from({ length: totalSlides }).map((_, index) => (
//                                             <button
//                                                 key={index}
//                                                 onClick={() => goToSlide(index)}
//                                                 className={`w-3 h-3 rounded-full transition-colors ${
//                                                     index === currentSlide 
//                                                         ? 'bg-blue-600' 
//                                                         : 'bg-gray-300 hover:bg-gray-400'
//                                                 }`}
//                                                 aria-label={`Go to slide ${index + 1}`}
//                                             />
//                                         ))}
//                                     </div>
//                                 )}
                                
//                                 {/* Progress bar for auto-slide */}
//                                 {totalSlides > 1 && (
//                                     <div className="mt-3 w-full bg-gray-200 rounded-full h-1">
//                                         <div 
//                                             className="bg-blue-600 h-1 rounded-full transition-all duration-1000 ease-linear"
//                                             style={{ width: `${((currentSlide + 1) / totalSlides) * 100}%` }}
//                                         />
//                                     </div>
//                                 )}
//                             </div>
//                         )}
//                     </div>
//                 </div>

//             </div>
//         </div>
//     );
// };

// export default ProductPage;

